import {useEffect, useState} from 'react';
import {AUDIT_EVENT_TYPE} from 'constants/audit-events';
import {endAuditSession, getAuditSessionId, getAuditSessionStartedAt, startAuditSession} from 'lib/audit-session';
import AuditApi from 'api/audit-api';
import {productsQueryKey} from 'constants/query';
import {reportDatadogError} from 'services/datadog.service';

function buildEvent({action, borrowerProfileId, data = {}, source = 'illustratorPage'}) {
  return {
    schemaVersion: 1,
    source,
    action,
    borrowerProfileId: borrowerProfileId ?? null,
    clientTimestamp: new Date().toISOString(),
    data,
  };
}

export function buildProductInputs(borrowerProfile = {}) {
  return {
    borrowerDOB: borrowerProfile?.DateOfBirth ?? null,
    propertyValue: borrowerProfile?.ValueOfProperty ?? null,
    propertyZipCode: borrowerProfile?.Zip ?? null,
    '1stMortgageBalance': borrowerProfile?.FirstLien ?? null,
    has2ndLiens: borrowerProfile?.IsThere2nd ?? null,
    '2ndLiens': borrowerProfile?.OtherLiens ?? null,
  };
}

function buildProductIllustrationData({productSelected, borrowerProfile, calculationId, ...extra}) {
  return {
    productSelected: productSelected ?? null,
    ...extra,
    productInputs: buildProductInputs(borrowerProfile),
    result: {
      calculationId: calculationId ?? null,
    },
  };
}

export function recordAuditEvent(payload) {
  AuditApi.saveAuditEvent({
    EventType: payload.eventType,
    CalculationId: payload.calculationId || undefined,
    BorrowerProfileId: payload.borrowerProfileId ?? undefined,
    EventData: payload.eventData,
  }).catch((error) => {
    reportDatadogError(error, {auditEventType: payload.eventType});
  });
}

/**
 * Reads the latest calculation for a product from the products cache.
 */
export function getCachedProductCalculation(queryClient, apiRoute) {
  return queryClient.getQueryData([productsQueryKey])?.data?.find((product) => product.ApiRoute === apiRoute)
    ?.calculation;
}

export function recordSelectedProductForIllustration({
  borrowerProfileId,
  borrowerProfile,
  productSelected,
  calculationId,
  source = 'illustratorPage',
}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.SelectedProductForIllustration,
    calculationId,
    borrowerProfileId,
    eventData: buildEvent({
      action: 'selected product for illustration',
      borrowerProfileId,
      source,
      data: buildProductIllustrationData({productSelected, borrowerProfile, calculationId}),
    }),
  });
}

export function recordUpdatingProductIllustration({
  borrowerProfileId,
  borrowerProfile,
  productSelected,
  calculationId,
  source = 'illustratorPage',
}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.UpdatingProductIllustration,
    calculationId,
    borrowerProfileId,
    eventData: buildEvent({
      action: 'updating main inputs for product illustration',
      borrowerProfileId,
      source,
      data: buildProductIllustrationData({productSelected, borrowerProfile, calculationId}),
    }),
  });
}

export function recordUpdatingProductDetailsForIllustration({
  borrowerProfileId,
  borrowerProfile,
  productSelected,
  calculationId,
  productDetails,
  source = 'illustratorPage',
}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.UpdatingProductDetailsForIllustration,
    calculationId,
    borrowerProfileId,
    eventData: buildEvent({
      action: 'updating product details for illustration',
      borrowerProfileId,
      source,
      data: buildProductIllustrationData({
        productSelected,
        borrowerProfile,
        calculationId,
        productDetails: productDetails ?? {},
      }),
    }),
  });
}

/**
 * The widget calculator has no single product: one update calculates every product, so the event carries the
 * widget inputs and every calculation returned. `calculationId` is the primary one, used to link the event.
 */
export function recordUpdatingWidgetCalculation({
  borrowerProfileId,
  calculationId,
  widgetInputs,
  calculations,
  source = 'widget',
}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.UpdatingWidgetCalculation,
    calculationId,
    borrowerProfileId,
    eventData: buildEvent({
      action: 'updating widget calculation',
      borrowerProfileId,
      source,
      data: {
        widgetInputs: widgetInputs ?? {},
        calculations: calculations ?? [],
      },
    }),
  });
}

export function recordChangingProductForIllustration({
  borrowerProfileId,
  borrowerProfile,
  productSelected,
  previousProduct,
  calculationId,
  source = 'illustratorPage',
}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.ChangingProductForIllustration,
    calculationId,
    borrowerProfileId,
    eventData: buildEvent({
      action: 'changing product for illustration',
      borrowerProfileId,
      source,
      // calculationId belongs to the product being changed away from
      data: buildProductIllustrationData({
        productSelected,
        previousProduct: previousProduct ?? null,
        borrowerProfile,
        calculationId,
      }),
    }),
  });
}

export function recordIllustratorSessionStarted({source = 'illustratorPage'} = {}) {
  recordAuditEvent({
    eventType: AUDIT_EVENT_TYPE.IllustratorSessionStarted,
    eventData: buildEvent({
      action: 'illustrator session started',
      source,
      data: {path: window.location.pathname},
    }),
  });
}

export function recordIllustratorSessionEnded({durationMs, source = 'illustratorPage'} = {}) {
  // The page may be unloading, so this uses a request that survives it.
  AuditApi.saveAuditEventKeepalive({
    EventType: AUDIT_EVENT_TYPE.IllustratorSessionEnded,
    EventData: buildEvent({
      action: 'illustrator session ended',
      source,
      data: {path: window.location.pathname, durationMs},
    }),
  });
}

/**
 * Keeps an audit session open while the calling page is mounted: it starts on entering the page and ends
 * when the user navigates away, closes the tab or refreshes.
 */
export function useAuditSession({source = 'illustratorPage'} = {}) {
  // Started during the first render so requests fired by child effects already carry the session id.
  useState(startAuditSession);

  useEffect(() => {
    const begin = () => {
      startAuditSession();
      recordIllustratorSessionStarted({source});
    };

    const end = () => {
      if (!getAuditSessionId()) return;

      recordIllustratorSessionEnded({durationMs: Date.now() - getAuditSessionStartedAt(), source});
      endAuditSession();
    };

    const onPageShow = (event) => {
      // restored from the back/forward cache after "pagehide" ended the previous session
      if (event.persisted) begin();
    };

    begin();
    window.addEventListener('pagehide', end);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', end);
      window.removeEventListener('pageshow', onPageShow);
      end();
    };
  }, [source]);
}

export function pickUpdatingProductIllustrationContext(calculations = [], products = []) {
  const recommendedProduct = products.find((p) => p?.calculation?.recommended);

  if (recommendedProduct?.calculation?.CalculationId) {
    return {
      productSelected: recommendedProduct.ApiRoute ?? null,
      calculationId: recommendedProduct.calculation.CalculationId,
    };
  }

  const firstCalc = calculations.find((c) => c.CalculationId);
  if (!firstCalc) {
    return {productSelected: null, calculationId: null};
  }

  const product = products.find((p) => p.ProductId === firstCalc.ProductId);

  return {
    productSelected: product?.ApiRoute ?? firstCalc.ApiRoute ?? firstCalc.Type ?? null,
    calculationId: firstCalc.CalculationId,
  };
}
